import { defineStore } from 'pinia'
import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import { sanitizeTextForSpeech } from '@/utils/speechText'

const base = import.meta.env.VITE_API_URL

// Monotonic token for TTS requests: each speak()/stopAudio() bumps it so a
// slow /ai/speak response from a superseded request never starts playing.
let speechSeq = 0

export const useAiAssistantStore = defineStore('aiAssistant', {
    state: () => ({
        widgetConfig: null,
        widgetTitle: 'AI Асистент',
        widgetWelcomeText: 'Запитайте про меню, страви або дієти',
        widgetPosition: 'right',
        widgetIcon: 'smiley',
        isOpen: false,
        messages: [],
        isLoading: false,
        sessionId: null,
        error: null,
        orderConfirming: false,
        orderError: null,
        orderSuccess: null,
        confirmedOrders: [],
        ttsEnabled: true,
        isSpeaking: false,
        currentAudio: null,
        currentAudioUrl: null,
        activeTab: 'chat',
    }),

    actions: {
        open() {
            this.isOpen = true
        },

        close() {
            this.stopAudio()
            this.isOpen = false
        },

        toggle() {
            this.isOpen = !this.isOpen
        },

        setTab(tab) {
            this.activeTab = tab
        },

        async sendMessage(text) {
            if (!text?.trim() || this.isLoading) return

            this.messages.push({
                role: 'user',
                content: text.trim(),
                timestamp: Date.now(),
            })

            this.isLoading = true
            this.error = null

            try {
                const lang = document.documentElement.lang || 'uk'

                const res = await axios.post(
                    `${base}/ai/chat`,
                    {
                        message: text.trim(),
                        sessionId: this.sessionId,
                        lang,
                    },
                    { withCredentials: true }
                )

                if (res.data.sessionId && !this.sessionId) {
                    this.sessionId = res.data.sessionId
                }

                this.messages.push({
                    role: 'assistant',
                    content: res.data.reply,
                    timestamp: Date.now(),
                })
                // speak() sanitizes (strips chips/markdown/ORDER) and no-ops if empty.
                this.speak(res.data.reply)
            } catch (err) {
                const msg =
                    err.response?.data?.error ||
                    'Не вдалося отримати відповідь. Спробуйте ще раз.'

                this.error = msg
                this.messages.push({
                    role: 'assistant',
                    content: `⚠️ ${msg}`,
                    timestamp: Date.now(),
                })
            } finally {
                this.isLoading = false
            }
        },

        async confirmOrder(orderData) {
            this.orderConfirming = true
            this.orderError = null
            this.orderSuccess = null
            this.confirmedOrders = []

            try {
                const authStore = useAuthStore()
                const authorId = authStore.user?.pers_id ?? null

                const res = await axios.post(
                    `${base}/ai/order`,
                    { order: orderData, authorId },
                    { withCredentials: true }
                )

                const orderKey = `${orderData.clientId}-${orderData.date}-${orderData.lunchId}`
                this.confirmedOrders.push(orderKey)
                this.orderSuccess = `Замовлення #${res.data.orderId} успішно записано!`
                return { success: true, orderId: res.data.orderId }
            } catch (err) {
                const msg = err.response?.data?.error || 'Не вдалося записати замовлення'
                this.orderError = msg
                return { success: false, error: msg }
            } finally {
                this.orderConfirming = false
            }
        },

        toggleTts() {
            this.ttsEnabled = !this.ttsEnabled
            if (!this.ttsEnabled) {
                this.stopAudio()
            }
        },

        stopAudio() {
            // Invalidate any in-flight speak() so a pending /ai/speak response
            // cannot start playing after stop/toggle-off/close.
            speechSeq++
            try { window.speechSynthesis?.cancel() } catch { /* noop */ }
            if (this.currentAudio) {
                try { this.currentAudio.pause() } catch { /* noop */ }
                this.currentAudio = null
            }
            if (this.currentAudioUrl) {
                try { URL.revokeObjectURL(this.currentAudioUrl) } catch { /* noop */ }
                this.currentAudioUrl = null
            }
            this.isSpeaking = false
        },

        async speak(text) {
            const clean = sanitizeTextForSpeech(text)
            if (!this.ttsEnabled || !clean) return
            this.stopAudio()
            const myId = ++speechSeq
            let url = null
            let audio = null
            try {
                this.isSpeaking = true
                const lang = document.documentElement.lang || 'uk'
                const res = await axios.post(
                    `${base}/ai/speak`,
                    { text: clean, lang },
                    { responseType: 'blob', withCredentials: true }
                )
                // Superseded by a newer speak()/stopAudio(), or TTS turned off while
                // awaiting: drop the blob and do not play (no URL created yet).
                if (myId !== speechSeq || !this.ttsEnabled) return
                url = URL.createObjectURL(res.data)
                audio = new Audio(url)
                this.currentAudio = audio
                this.currentAudioUrl = url
                audio.onended = () => {
                    this.isSpeaking = false
                    if (this.currentAudio === audio) this.currentAudio = null
                    try { URL.revokeObjectURL(url) } catch { /* noop */ }
                    if (this.currentAudioUrl === url) this.currentAudioUrl = null
                }
                audio.onerror = () => {
                    this.isSpeaking = false
                    if (this.currentAudio === audio) this.currentAudio = null
                    try { URL.revokeObjectURL(url) } catch { /* noop */ }
                    if (this.currentAudioUrl === url) this.currentAudioUrl = null
                }
                await audio.play()
            } catch {
                if (audio) {
                    try { audio.pause() } catch { /* noop */ }
                    if (this.currentAudio === audio) this.currentAudio = null
                }
                if (url) {
                    try { URL.revokeObjectURL(url) } catch { /* noop */ }
                    if (this.currentAudioUrl === url) this.currentAudioUrl = null
                }
                this.isSpeaking = false
            }
        },

        clearHistory() {
            this.messages = []
            this.sessionId = null
            this.error = null
            this.orderError = null
            this.orderSuccess = null
            this.confirmedOrders = []
        },

        async fetchWidgetConfig() {
            try {
                const res = await axios.get(`${base}/ai/widget-config`)
                const cfg = res.data
                this.widgetConfig = cfg
                this.widgetTitle = cfg.title || 'AI Асистент'
                this.widgetWelcomeText = cfg.welcomeText || 'Запитайте про меню, страви або дієти'
                this.widgetPosition = cfg.position || 'right'
                this.widgetIcon = cfg.icon || 'smiley'
                const root = document.documentElement
                root.style.setProperty('--widget-primary',      cfg.primaryColor  || '#f0a500')
                root.style.setProperty('--widget-bot-bubble',   cfg.botBubbleBg   || '#f0f0f0')
                root.style.setProperty('--widget-user-bubble',  cfg.userBubbleBg  || '#f0a500')
                root.style.setProperty('--widget-radius',       (cfg.borderRadius || '20') + 'px')
            } catch {
                // fallback: defaults already in CSS
            }
        },
    },
})
